import { DatePipe } from '@angular/common';
import { ChangeDetectorRef, Component, forwardRef, Injector, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR, NgControl, Validators } from '@angular/forms';
import { OverlayPanel } from 'primeng/overlaypanel';
import { Subscription } from 'rxjs';
import { DateTimePeriod } from '../shared/entities/dueDateTimePeriod/dueDateTimePeriod';
import { NxEditCalendarInputValue } from './edit-calendar-form-control.component';

interface TimeOption {
    label: string;
    value: string;
  }

@Component({
    selector: 'nx-edit-dateTime-input-v2',
    template: `
    <ng-container [formGroup]="dateTimePeriodForm"> 
    <div class="nx-edit-dateTime-input-v2">
        <label for="text">{{label}}<ng-container *ngIf=isRequired()>*</ng-container></label>
        <div class="flex flex-row" (click)="$event.stopPropagation()">
            <div
                class="input previewValue"
                (click)="toggleOverlayPanel($event)"
                [ngClass]="{'focus': overlayPanelVisible}">
                <ng-container
                    *ngIf="this.buildDueDateTimeString()">
                    {{this.buildDueDateTimeString()}}
                </ng-container>
                <span class="placeholder"
                    *ngIf="!this.buildDueDateTimeString()">
                    {{placeholder}}
                </span>
            </div>
            
            <p-overlayPanel 
                [dismissable]="dismissable"
                (onShow)="onShow()"
                (onHide)="onHide()"
                #overlayPanel>
                <div class="flex flex-col form nx-edit-dateTime">
                    <div class="flex flex-row basis-full">
                        <div class="basis-1/2 flex justify-start">
                            <p-dropdown
                                [style]="{'width':'7.5rem'}"
                                formControlName="modifier"
                                [options]="_modifierOptions"   
                                optionLabel="name"
                                [virtualScroll]="false"
                                [filter]="false"
                                [panelStyleClass]="'nx-edit-select'"
                                [placeholder]="'test'"/>
                        </div>
                        <div class="basis-1/2 flex justify-end items-center">
                            <nx-default-button
                                (click)="togglePrecision($event)"
                                [tooltip]="'Zeit hinzufügen'"
                                [style]="isPrecisionDateTime() ? 'accent' : 'dark'"
                                [icon]="'icon-time'">
                            </nx-default-button>    
                        </div>
                    </div>
                    <div>
                        <nx-edit-calendar-input
                            class="w-full p-datepicker-inline mb-1"
                            formControlName="calendarFormControl"
                            [selectionMode]="getCalendarSelectionMode()"
                            [showTime]="getShowTime()"
                            [style]="{'width':'270px'}"
                            id="fromDateTime"
                            (blockOverlayDismiss)="blockOverlayDismiss()"
                            (unblockOverlayDismiss)="unblockOverlayDismiss()"
                            dateFormat="dd.mm.yy">
                        </nx-edit-calendar-input>
                    </div>
                    <div class="flex justify-end">
                        <nx-default-button
                            [style]="'light'"
                            class="ml-2"
                            (onClick)="clear()"
                            [label]="'Löschen'">
                        </nx-default-button>
                        <nx-default-button
                            [style]="'accent'"
                            class="ml-2"
                            (onClick)="accept()"
                            [disabled]="!previewDueDateTime"
                            [label]="'Anwenden'">
                        </nx-default-button>
                    </div>
                </div>
            </p-overlayPanel>

        </div>
    </div>
    </ng-container>
  `,
    styles: [`
        .form > *:not(:last-child) {
            margin:0 !important;
        }
        .placeholder {
            color: #888484;
        }
  `],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => EditDateTimePeriodInputV2Component),
            multi: true
        }
    ]
})
export class EditDateTimePeriodInputV2Component implements ControlValueAccessor, OnInit, OnDestroy {

    @ViewChild('overlayPanel') overlayPanel: OverlayPanel;

    dismissable = true;
    accepted = false;

    ngControl: NgControl;
    isDisabled: boolean;
    @Input() label: string;
    @Input() placeholder: string;

    previewDueDateTime: DateTimePeriod;
    overlayPanelVisible: boolean = false;

    private onChange: (value: DateTimePeriod) => void;
    private onTouched: () => void;

    // modifier
    _modifierOptions = [
        { id: 'BEFORE', name: 'vor' },
        { id: 'AFTER', name: 'nach' },
        { id: 'EXACT', name: 'exakt' },
        { id: 'BETWEEN', name: 'zwischen' }
      ];
    modifierFormControlSubscription: Subscription;
    modifierFormControl: FormControl;
    precisionFormControl: FormControl;
    calendarFormControl: FormControl;

    public dateTimePeriodForm: FormGroup;
    public dateTimePeriodFormSubscription: Subscription;

    timeOptions: TimeOption[] = [];

    constructor(
        private _injector: Injector,
        private cd: ChangeDetectorRef,
        public datePipe: DatePipe,
    ) {
        this.timeOptions = this.generateTimeOptions();
    }

    ngOnDestroy(): void {
        this.modifierFormControlSubscription?.unsubscribe();
        this.dateTimePeriodFormSubscription?.unsubscribe();
    }


    getCalendarSelectionMode(): "single" | "range"{
        return this.modifierFormControl?.value?.id === 'BETWEEN' ? 'range' : 'single';
    }

    getShowTime(): boolean {
        return this.precisionFormControl?.value === 'DATE_TIME';
    }

    ngOnInit() {
        this.ngControl = this._injector.get(NgControl);
        this.ngControl.valueAccessor = this;
        this.dateTimePeriodForm = new FormGroup({});
        let idFormControl = new FormControl(undefined, []);
        this.dateTimePeriodForm.addControl('id', idFormControl);
        this.modifierFormControl = new FormControl(this._modifierOptions[0], [Validators.required]);
        this.dateTimePeriodForm.addControl('modifier', this.modifierFormControl);
        this.precisionFormControl = new FormControl('DATE', [Validators.required]);
        this.dateTimePeriodForm.addControl('precision', this.precisionFormControl);
        this.calendarFormControl = new FormControl(undefined, [Validators.required]);
        this.dateTimePeriodForm.addControl('calendarFormControl', this.calendarFormControl);
        this.dateTimePeriodFormSubscription = this.dateTimePeriodForm.valueChanges.subscribe( (value) => {
            this.onInputChange();
        });
    }

    blockOverlayDismiss() {
        this.overlayPanel.unbindDocumentClickListener();
    }

    unblockOverlayDismiss() {
        setTimeout(() => {
            this.overlayPanel.bindDocumentClickListener();
        }, 1);
    }

    onInputChange(){
        this.accepted = false;
        let value: DateTimePeriod = undefined;
        let dateTimeModifierValue = this.dateTimePeriodForm.get('modifier')?.value;
        if(dateTimeModifierValue != null && dateTimeModifierValue.id === 'BETWEEN') {
            let calendarValue = this.dateTimePeriodForm.get('calendarFormControl')?.value as NxEditCalendarInputValue;
            if(calendarValue && calendarValue.fromDateTime && calendarValue.toDateTime) {
                value = new DateTimePeriod();
                value.id = this.dateTimePeriodForm.get('id')?.value;
                value.fromDateTime = calendarValue.fromDateTime;
                value.toDateTime = calendarValue.toDateTime;
                value.modifier = dateTimeModifierValue.id;
                // we use the acutal precision the user really has entered and not only selected
                value.precision = calendarValue.precision;
            }
        } else if (dateTimeModifierValue!= null) {
            // every other modifier
            let calendarValue = this.dateTimePeriodForm.get('calendarFormControl')?.value as NxEditCalendarInputValue;
            if(calendarValue && calendarValue.dateTime) {
                value = new DateTimePeriod();
                value.id = this.dateTimePeriodForm.get('id')?.value;
                value.dateTime = calendarValue.dateTime;
                value.modifier = dateTimeModifierValue.id;
                // we use the acutal precision the user really has entered and not only selected
                value.precision = calendarValue.precision;
            }
        }
        this.previewDueDateTime = value;
        // if(this.onChange) {
        //     this.onChange(value);
        // }
    }

    accept() {
        this.accepted = true;
        if(this.onChange) {
            this.onChange(this.previewDueDateTime);
        }
        this.overlayPanel.hide();
    }

    clear() {
        this.previewDueDateTime = undefined;
        this.dateTimePeriodForm.reset();
        this.modifierFormControl.setValue(this._modifierOptions[0], { emitEvent: false });
        this.precisionFormControl.setValue('DATE', { emitEvent: false });
        this.onChange(undefined);
    }

    togglePrecision($event: MouseEvent) {
        if(this.precisionFormControl?.value === 'DATE') {
            this.precisionFormControl?.setValue('DATE_TIME');
        } else {
            this.precisionFormControl?.setValue('DATE');
        }
    }

    writeValue(dateTimePeriod: DateTimePeriod): void {
        if(dateTimePeriod) {
            // we cannot use patch value for the modifier, because the incoming value is not the
            // object we need to push into the multi select and the modifier must be set
            // before the other values, because, depending on the modifier, other form fields
            // are needed
            if(dateTimePeriod.id) {
                this.dateTimePeriodForm.get('id')?.setValue(dateTimePeriod.id);
            }
            if(dateTimePeriod.modifier) {
                this.dateTimePeriodForm.get('modifier')?.setValue(this._modifierOptions.find(option => option.id === dateTimePeriod.modifier));
            }
            if(dateTimePeriod.precision) {
                this.dateTimePeriodForm.get('precision')?.setValue(dateTimePeriod.precision);
            }
            if(dateTimePeriod.precision &&
                (dateTimePeriod.dateTime || (dateTimePeriod.fromDateTime && dateTimePeriod.toDateTime))) {
                    let calendarValue : NxEditCalendarInputValue = {
                        dateTime: dateTimePeriod.dateTime,
                        fromDateTime: dateTimePeriod.fromDateTime,
                        toDateTime: dateTimePeriod.toDateTime,
                        precision: dateTimePeriod.precision
                    }
                this.dateTimePeriodForm.get('calendarFormControl')?.setValue(calendarValue);
                }
        } else {
            //this.dateTimePeriodForm.reset();
        }
    }

    registerOnChange(fn: (value: DateTimePeriod) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        // Optional: Implementieren Sie die Logik zum Deaktivieren des Steuerelements
        this.isDisabled = isDisabled;
    }

    isRequired(): boolean {
        return this.ngControl?.control?.hasValidator(Validators.required);
    }

    isBetween(): boolean {
        return false;
    }

    //____
    toggleOverlayPanel($event: MouseEvent) {
        this.overlayPanel.toggle($event)
    }

    isPrecisionDateTime(): boolean {
        return this.dateTimePeriodForm.get('precision')?.value === 'DATE_TIME'; 
    }

    generateTimeOptions(): TimeOption[] {
        const options: TimeOption[] = [];
        
        for (let hour = 0; hour < 24; hour++) {
          for (let minute = 0; minute < 60; minute += 30) {
            const hourStr = hour.toString().padStart(2, '0');
            const minuteStr = minute.toString().padStart(2, '0');
            const timeStr = `${hourStr}:${minuteStr}`;
            
            options.push({
              label: timeStr,
              value: timeStr
            });
          }
        }
        
        return options;
    }

    // TODO: this should be moved into a service - but: Currently dateTimePeriod is implemented in both: core and frontend-web
    public buildDueDateTimeString() {
        if (this.previewDueDateTime) {
            if(this.previewDueDateTime.modifier === 'BETWEEN') {
                if(this.previewDueDateTime.precision === 'DATE_TIME') {
                    return this.datePipe.transform(this.previewDueDateTime.fromDateTime, "EEE, dd.MM.yy HH:mm") + ' - ' + this.datePipe.transform(this.previewDueDateTime.toDateTime, "EEE, dd.MM.yy HH:mm");
                } else {
                    return this.datePipe.transform(this.previewDueDateTime.fromDateTime, "EEE, dd.MM.yy") + ' - ' + this.datePipe.transform(this.previewDueDateTime.toDateTime, "EEE, dd.MM.yy");
                }
            } else {
                let modifierString: string = undefined;
                switch(this.previewDueDateTime.modifier) {
                    case 'BEFORE':
                        modifierString = 'vor ';
                        break;
                    case 'AFTER':
                        modifierString = 'nach ';
                        break;
                    default:
                        modifierString = '';
                }
                if(this.previewDueDateTime.precision === 'DATE_TIME') {
                    return this.datePipe.transform(this.previewDueDateTime.dateTime, "EEE, dd.MM.yy") + ' ' + modifierString + this.datePipe.transform(this.previewDueDateTime.dateTime, "HH:mm");
                } else {
                    return modifierString + this.datePipe.transform(this.previewDueDateTime.dateTime, "EEE, dd.MM.yy");
                }
            }
        }
         return undefined;
      }

    onShow(){
        this.overlayPanelVisible = true
    }

    onHide(){
        this.overlayPanelVisible = false;
        if(!this.accepted) {
            console.log(this.dateTimePeriodForm);
            this.clear();
        }
        
    }
}